import { CLIENT_SIZES } from 'constants/clientSizes';

/**
 * The arithmetic of the agency's yearly plan (server/crm/crmGoalsApi.js), as
 * the « Plan d'action » of the « Tableau pilotage » counts it: an ETP is worth
 * the SMIC of a month (151,67 h) × the average billing coefficient; a client
 * starts at a few ETP the month it signs and grows each month, up to the ETP
 * of its size; the plan says how many clients of each size sign each month.
 */
export const HOURS_PER_MONTH = 151.67;
export const MONTHS = 12;

export const etpPrice = (smic, coefficient) =>
  smic * HOURS_PER_MONTH * coefficient;

// The ETP of a client `age` months after it signed.
export const clientEtp = (size, age) =>
  Math.min(size.max, size.start * (1 + size.growth / 100) ** age);

const countsOf = (goals, territory, size) =>
  goals.plan?.[territory]?.[size] ?? Array(MONTHS).fill(0);

// The ETP of a row (a territory and a size) each month: every client signed
// so far, each on its own ramp.
export const rowEtp = (goals, territory, size) => {
  const counts = countsOf(goals, territory, size);
  return Array.from({ length: MONTHS }, (_, month) =>
    counts
      .slice(0, month + 1)
      .reduce(
        (total, signed, at) =>
          total + (signed || 0) * clientEtp(goals.sizes[size], month - at),
        0,
      ),
  );
};

const sum = (values) => values.reduce((total, value) => total + value, 0);
const addMonths = (a, b) => a.map((value, month) => value + b[month]);

// A row's clients, its revenue each month and over the year.
export const rowPlan = (goals, territory, size, price) => {
  const months = rowEtp(goals, territory, size).map((etp) => etp * price);
  return {
    clients: sum(countsOf(goals, territory, size).map((n) => n || 0)),
    months,
    revenue: sum(months),
  };
};

export const territoryPlan = (goals, territory, price) =>
  CLIENT_SIZES.map((size) => rowPlan(goals, territory, size, price)).reduce(
    (total, row) => ({
      clients: total.clients + row.clients,
      months: addMonths(total.months, row.months),
      revenue: total.revenue + row.revenue,
    }),
    { clients: 0, months: Array(MONTHS).fill(0), revenue: 0 },
  );

export const agencyPlan = (goals, territories, price) =>
  territories
    .map((territory) => territoryPlan(goals, territory, price))
    .reduce(
      (total, row) => ({
        clients: total.clients + row.clients,
        months: addMonths(total.months, row.months),
        revenue: total.revenue + row.revenue,
      }),
      { clients: 0, months: Array(MONTHS).fill(0), revenue: 0 },
    );

// What the plan expects done by `day` of its year: the clients signed in the
// months begun, the revenue of the months gone plus the share of this one.
export const expectedBy = (plan, counts, year, day = new Date()) => {
  if (day < new Date(year, 0, 1)) return { clients: 0, revenue: 0 };
  if (day >= new Date(year + 1, 0, 1))
    return { clients: sum(counts), revenue: plan.revenue };
  const month = day.getMonth();
  const share = (day.getDate() - 1) / new Date(year, month + 1, 0).getDate();
  return {
    clients: sum(counts.slice(0, month + 1)),
    revenue: sum(plan.months.slice(0, month)) + plan.months[month] * share,
  };
};
